import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { clientService } from '../../../services/clientService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const PERMISSION_TOGGLES = [
  { key: 'canViewTimeline', label: 'Timeline', icon: 'Activity', description: 'Activity feed & events' },
  { key: 'canViewPhotos', label: 'Photos', icon: 'Images', description: 'Site photo gallery' },
  { key: 'canViewDocuments', label: 'Documents', icon: 'FileText', description: 'Shared documents' },
  { key: 'canViewReports', label: 'Reports', icon: 'ShieldCheck', description: 'Compliance reports' },
];

const DEFAULT_PERMISSIONS = {
  canViewTimeline: true,
  canViewPhotos: true,
  canViewDocuments: true,
  canViewReports: true,
};

const ClientAccessSection = ({ siteId, siteData }) => {
  const { userProfile } = useAuth();
  const [shares, setShares] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New share form state
  const [selectedClientId, setSelectedClientId] = useState('');
  const [newPermissions, setNewPermissions] = useState({ ...DEFAULT_PERMISSIONS });
  const [notes, setNotes] = useState('');

  // Inline edit state: shareId -> permissions object
  const [editingPermissions, setEditingPermissions] = useState({});
  const [savingShareId, setSavingShareId] = useState(null);

  // Invite by email state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const loadData = useCallback(async () => {
    if (!siteId || !userProfile?.company_id) return;
    setLoading(true);
    setError('');
    try {
      const [sharesRes, clientsRes] = await Promise.all([
        clientService?.getSharesForSite(siteId),
        clientService?.getClientUsers(userProfile?.company_id),
      ]);
      if (sharesRes?.error) throw sharesRes?.error;
      if (clientsRes?.error) throw clientsRes?.error;
      setShares(sharesRes?.data || []);
      setClientUsers(clientsRes?.data || []);
    } catch (err) {
      console.error('[ClientAccessSection] load data failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading client access data: ${err?.message}`);
      } else {
        setError(`Failed to load client access data: ${err?.message || 'Unknown error'}. Please refresh the page.`);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, userProfile?.company_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clients already shared — exclude from dropdown
  const sharedClientIds = shares?.map(s => s?.client?.id);
  const availableClients = clientUsers?.filter(c => !sharedClientIds?.includes(c?.id));

  const handleAddShare = async () => {
    if (!selectedClientId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { error: err } = await clientService?.shareSiteWithClient({
        siteId,
        clientUserId: selectedClientId,
        companyId: userProfile?.company_id,
        notes,
        permissions: newPermissions,
      });
      if (err) throw err;
      setSuccess('Client access granted successfully.');
      setSelectedClientId('');
      setNotes('');
      setNewPermissions({ ...DEFAULT_PERMISSIONS });
      await loadData();
    } catch (err) {
      console.error('[ClientAccessSection] share site failed:', { code: err?.code, message: err?.message });
      if (err?.code === '23505') {
        setError('This client already has access to this site.');
      } else if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied sharing this site: ${err?.message}`);
      } else {
        setError(`Failed to share site: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail?.trim()) return;
    setInviting(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('invite-client', {
        body: {
          email: inviteEmail?.trim(),
          companyId: userProfile?.company_id,
          companyName: siteData?.company_name || userProfile?.company_name || '',
          siteId,
          siteName: siteData?.site_name || '',
          invitedBy: userProfile?.id,
        },
      });
      // fnError is a FunctionsError from supabase.functions.invoke (already
      // an Error-shaped object with .message). Throw it directly so .name
      // and any additional fields survive.
      if (fnError) throw fnError;
      // data?.error is a plain string returned in the function's response
      // body (different shape from PostgrestError). Wrap so .message works.
      if (data?.error) throw new Error(data.error);
      setInviteSuccess(`Invite sent to ${inviteEmail?.trim()}`);
      setInviteEmail('');
    } catch (err) {
      console.error('[ClientAccessSection] send invite failed:', { code: err?.code, message: err?.message });
      setInviteError(err?.message ? `Failed to send the invite: ${err.message}` : 'Failed to send the invite. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleTogglePermission = (shareId, permKey) => {
    setEditingPermissions(prev => {
      const current = prev?.[shareId] || getSharePermissions(shareId);
      return { ...prev, [shareId]: { ...current, [permKey]: !current?.[permKey] } };
    });
  };

  const getSharePermissions = (shareId) => {
    const share = shares?.find(s => s?.id === shareId);
    return {
      canViewTimeline: share?.can_view_timeline ?? true,
      canViewPhotos: share?.can_view_photos ?? true,
      canViewDocuments: share?.can_view_documents ?? true,
      canViewReports: share?.can_view_reports ?? true,
    };
  };

  const getEffectivePermissions = (shareId) => {
    return editingPermissions?.[shareId] || getSharePermissions(shareId);
  };

  const handleSavePermissions = async (shareId) => {
    const perms = editingPermissions?.[shareId];
    if (!perms) return;
    setSavingShareId(shareId);
    setError('');
    setSuccess('');
    try {
      const { error: err } = await clientService?.updateSharePermissions(shareId, perms);
      if (err) throw err;
      setSuccess('Permissions updated.');
      setEditingPermissions(prev => { const n = { ...prev }; delete n?.[shareId]; return n; });
      await loadData();
    } catch (err) {
      console.error('[ClientAccessSection] update permissions failed:', { shareId, code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied updating share: ${err?.message}`);
      } else {
        setError(`Failed to update permissions: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setSavingShareId(null);
    }
  };

  const handleRemoveShare = async (shareId, clientName) => {
    if (!window.confirm(`Remove ${clientName}'s access to this site?`)) return;
    setError('');
    setSuccess('');
    try {
      const { error: err } = await clientService?.removeSiteShare(shareId);
      if (err) throw err;
      setSuccess('Client access removed.');
      await loadData();
    } catch (err) {
      console.error('[ClientAccessSection] remove access failed:', { shareId, code: err?.code, message: err?.message });
      if (err?.code === 'P0001') {
        // Last-share-delete trigger fired. Surface the actionable copy.
        setError(`${clientName} only has access to this one site. A client must be assigned to at least one site — add ${clientName} to another site first, then remove this share. (Or delete the client's account entirely if they no longer need access.)`);
      } else if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied removing share: ${err?.message}`);
      } else {
        setError(`Failed to remove access: ${err?.message || 'Unknown error'}`);
      }
    }
  };

  const hasUnsavedChanges = (shareId) => !!editingPermissions?.[shareId];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Icon name="Loader2" size={24} className="animate-spin text-accent" />
        <span className="ml-2 text-sm text-muted-foreground">Loading client access...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Icon name="Users" size={18} className="text-purple-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Client Access</h3>
          <p className="text-xs text-muted-foreground">Control which clients can view this site and what they can see</p>
        </div>
      </div>
      {/* Feedback */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <Icon name="AlertCircle" size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
          <Icon name="CheckCircle2" size={15} className="flex-shrink-0" />
          {success}
        </div>
      )}
      {/* Invite Client by Email */}
      <div className="bg-muted/30 border border-border rounded-xl p-5">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Mail" size={15} className="text-purple-600" />
          Invite Client by Email
        </h4>
        <p className="text-xs text-muted-foreground mb-3">Send an invitation email to a new client. They will create their account and immediately see this site.</p>
        <div className="flex gap-2">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e?.target?.value)}
            placeholder="client@company.com"
            className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400/30 text-foreground placeholder:text-muted-foreground"
            onKeyDown={e => e?.key === 'Enter' && handleSendInvite()}
          />
          <button
            onClick={handleSendInvite}
            disabled={!inviteEmail?.trim() || inviting}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {inviting ? (
              <><Icon name="Loader2" size={14} className="animate-spin" /> Sending...</>
            ) : (
              <><Icon name="Send" size={14} /> Send Invite</>
            )}
          </button>
        </div>
        {inviteSuccess && (
          <div className="flex items-center gap-2 mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
            <Icon name="CheckCircle2" size={14} className="flex-shrink-0" />
            {inviteSuccess}
          </div>
        )}
        {inviteError && (
          <div className="flex items-center gap-2 mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
            {inviteError}
          </div>
        )}
      </div>
      {/* Add New Client Share */}
      <div className="bg-muted/30 border border-border rounded-xl p-5">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="UserPlus" size={15} className="text-accent" />
          Grant Access to a Client
        </h4>

        {availableClients?.length === 0 && clientUsers?.length > 0 ? (
          <p className="text-sm text-muted-foreground">All client users already have access to this site.</p>
        ) : clientUsers?.length === 0 ? (
          <p className="text-sm text-muted-foreground">No client users found in your organisation. Invite clients first.</p>
        ) : (
          <div className="space-y-4">
            {/* Client selector */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Select Client</label>
              <select
                value={selectedClientId}
                onChange={e => setSelectedClientId(e?.target?.value)}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground"
              >
                <option value="">-- Choose a client user --</option>
                {availableClients?.map(c => (
                  <option key={c?.id} value={c?.id}>{c?.full_name} ({c?.email})</option>
                ))}
              </select>
            </div>

            {/* Permission toggles for new share */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">Module Access</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PERMISSION_TOGGLES?.map(toggle => (
                  <button
                    key={toggle?.key}
                    type="button"
                    onClick={() => setNewPermissions(prev => ({ ...prev, [toggle?.key]: !prev?.[toggle?.key] }))}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center ${
                      newPermissions?.[toggle?.key]
                        ? 'border-accent bg-accent/10 text-accent' :'border-border bg-background text-muted-foreground'
                    }`}
                  >
                    <Icon name={toggle?.icon} size={18} />
                    <span className="text-xs font-medium">{toggle?.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      newPermissions?.[toggle?.key] ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'
                    }`}>
                      {newPermissions?.[toggle?.key] ? 'On' : 'Off'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notes (optional)</label>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e?.target?.value)}
                placeholder="e.g. Shared for Q1 review"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground placeholder:text-muted-foreground"
              />
            </div>

            <button
              onClick={handleAddShare}
              disabled={!selectedClientId || saving}
              className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="UserPlus" size={15} />}
              {saving ? 'Granting Access...' : 'Grant Access'}
            </button>
          </div>
        )}
      </div>
      {/* Existing Shares List */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Users" size={15} className="text-muted-foreground" />
          Clients with Access
          <span className="ml-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{shares?.length}</span>
        </h4>

        {shares?.length === 0 ? (
          <div className="text-center py-8 bg-muted/20 rounded-xl border border-dashed border-border">
            <Icon name="UserX" size={28} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No clients have access to this site yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {shares?.map(share => {
              const perms = getEffectivePermissions(share?.id);
              const isDirty = hasUnsavedChanges(share?.id);
              const isSaving = savingShareId === share?.id;

              return (
                <div key={share?.id} className="bg-card border border-border rounded-xl p-4">
                  {/* Client info row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={15} className="text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{share?.client?.full_name}</p>
                        <p className="text-xs text-muted-foreground">{share?.client?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Shared {new Date(share?.shared_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <button
                        onClick={() => handleRemoveShare(share?.id, share?.client?.full_name)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Remove access"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  </div>
                  {/* Permission toggles */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {PERMISSION_TOGGLES?.map(toggle => (
                      <button
                        key={toggle?.key}
                        type="button"
                        onClick={() => handleTogglePermission(share?.id, toggle?.key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-left ${
                          perms?.[toggle?.key]
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700' :'border-border bg-muted/40 text-muted-foreground'
                        }`}
                      >
                        <Icon name={perms?.[toggle?.key] ? 'ToggleRight' : 'ToggleLeft'} size={16} />
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{toggle?.label}</p>
                          <p className="text-xs opacity-70">{perms?.[toggle?.key] ? 'Visible' : 'Hidden'}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {/* Save button — only shown when there are unsaved changes */}
                  {isDirty && (
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                      <button
                        onClick={() => handleSavePermissions(share?.id)}
                        disabled={isSaving}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground rounded-lg text-xs font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
                      >
                        {isSaving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Save" size={13} />}
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={() => setEditingPermissions(prev => { const n = { ...prev }; delete n?.[share?.id]; return n; })}
                        className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Discard
                      </button>
                      <span className="text-xs text-amber-600 flex items-center gap-1">
                        <Icon name="AlertCircle" size={12} />
                        Unsaved changes
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientAccessSection;
